import React, { useState } from "react";
import { ReportAssignmentRow, Bom, ProjectReports, ReportTypeDefinition } from "./types";
import { dueRangeLabel } from "./logic";
import { RaIcon } from "./RaIcon";

interface ReportPlanSectionProps {
  reportTypes: ReportTypeDefinition[];
  boms: Bom[];
  rows: ReportAssignmentRow[];
  /** Project-wise report mapping (available vs applicable reports), when loaded. */
  mapping?: ProjectReports;
}

/**
 * Section 1 — Report plan: a read-only summary of how many reports of each
 * type every BOM needs. The reports are loaded automatically for the project
 * (existing report results, otherwise the reports matching the BOM's test
 * sequences); nothing is selected by hand.
 */
export const ReportPlanSection: React.FC<ReportPlanSectionProps> = ({
  reportTypes,
  boms,
  rows,
  mapping,
}) => {
  const [open, setOpen] = useState(true);

  return (
    <section className="sec sec-plan">
      <div className="sh">
        <span className="num">1</span>
        <div>
          <h2>Report plan</h2>
          <div className="sub">
            How many reports each BOM needs. They are loaded automatically for this project,
            based on its existing reports and test sequences.
          </div>
        </div>
        <span className="sp" />
        <button
          type="button"
          className="btn sm"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Collapse" : "Show plan"}
          <RaIcon name="chev" className={open ? "chev up" : "chev"} />
        </button>
      </div>

      {!open ? (
        <div className="psum">
          {boms.map((b) => (
            <span key={b.id}>
              <b>{rows.filter((r) => r.bomId === b.id).length}</b> {b.name}
            </span>
          ))}
          <span className="ps-t">
            <b>{rows.length}</b> reports in total
          </span>
          <span className="lk">
            <RaIcon name="lock" />
            Plan created
          </span>
        </div>
      ) : (
        <>
          <div className="plan-wrap">
            <table className="plan">
              <thead>
                <tr>
                  <th>Report</th>
                  {boms.map((b) => (
                    <th key={b.id} className="c">
                      {b.name}
                    </th>
                  ))}
                  <th>Due</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {reportTypes.map((t) => {
                  const typeRows = rows.filter((r) => r.typeKey === t.key);
                  const due = dueRangeLabel(typeRows);
                  return (
                    <tr key={t.key}>
                      <td className="rn">
                        <b>{t.name}</b>
                        <span>{t.description}</span>
                      </td>
                      {boms.map((b) => {
                        const n = typeRows.filter((r) => r.bomId === b.id).length;
                        return (
                          <td key={b.id} className="c">
                            {n ? <span className="dot y">{n}</span> : <span className="dot n">–</span>}
                          </td>
                        );
                      })}
                      <td className="tl">{typeRows.some((r) => r.dueDate) || "–"}</td>
                      <td>
                        <span className="src">
                          <RaIcon name="lock" />
                          {typeRows.some((r) => r.source === "report_result")
                            ? "Existing report"
                            : "Test sequence"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="ptot">
            <span>
              <b>{rows.length}</b> reports in the plan
            </span>
            {mapping && (
              <span>
                {mapping.summary.applicable} of {mapping.summary.available} available report types
                apply to this project
              </span>
            )}
            <span className="lk">
              <RaIcon name="lock" />
              A report beyond the plan will come in as a change order with its own BOM (e.g. BOM 1
              - CO-1).
            </span>
          </div>
        </>
      )}
    </section>
  );
};
